<template>
  <div class="sub-container">
    <v-card
      class="header-card mb-4"
      color="#433876"
      variant="flat"
      rounded="lg"
    >
      <v-card-actions class="py-4 px-4">
        <span class="head-sub-text">
          <v-icon class="mx-2">mdi-account</v-icon>All Subscribers
        </span>
        <v-spacer />

        <v-btn
          v-if="screenSize === 'Large'"
          color="#38a4f8"
          variant="flat"
          class="text-none mr-2"
          size="small"
          rounded="lg"
          :to="{ name: 'importSubs' }"
        >
          <span class="btn-text">
            <v-icon class="mx-1">mdi-import</v-icon>Import subscribers
          </span>
        </v-btn>

        <v-btn
          color="success"
          variant="flat"
          class="text-none"
          size="small"
          rounded="lg"
          @click="redirectToAdd()"
        >
          <span class="btn-text">
            <v-icon class="mx-1">mdi-account-plus</v-icon> Add Subscriber
          </span>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Filters -->
    <v-card class="filter-card mb-4" variant="flat" rounded="lg">
      <v-row no-gutters class="pa-4" align="start">
        <v-col cols="12" sm="6" md="3" class="pr-4 mb-2 mb-md-0">
          <p class="field-label">Groups</p>
          <v-autocomplete
            v-model="formData.group_id"
            :items="lists.length > 0 ? lists : []"
            item-title="name"
            item-value="id"
            label="Select"
            chips
            closable-chips
            density="compact"
            hide-details
            variant="outlined"
          ></v-autocomplete>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="pr-4 mb-2 mb-md-0">
          <p class="field-label">Recipient</p>
          <v-text-field
            v-model="formData.email"
            label="Search by email"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-col>

        <v-col
          cols="12"
          md="4"
          class="d-flex flex-wrap align-center mb-2 mb-md-0"
        >
          <v-checkbox
            color="indigo-darken-3"
            label="Subscribed"
            class="status-check"
            hide-details
            v-model="formData.is_subscribed"
          ></v-checkbox>

          <v-checkbox
            v-model="formData.unsubscribed"
            label="Unsubscribed"
            color="indigo-darken-3"
            class="status-check"
            hide-details
          ></v-checkbox>

          <v-checkbox
            color="indigo-darken-3"
            label="Blacklisted"
            class="status-check"
            hide-details
            v-model="formData.is_blacklisted"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" md="2">
          <p class="field-label">
            Quick search
            <v-tooltip
              location="top"
              text="Searches only the subscribers currently loaded on this page"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props" size="14" class="ml-1"
                  >mdi-information-outline</v-icon
                >
              </template>
            </v-tooltip>
          </p>
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            single-line
          />
        </v-col>
      </v-row>

      <v-divider />

      <v-row no-gutters class="pa-4" align="center">
        <v-btn
          color="#50568e"
          variant="flat"
          class="text-none mr-2"
          size="small"
          rounded="lg"
          prepend-icon="mdi-magnify"
          @click="submitFilters()"
        >
          <span class="btn-text">Search</span>
        </v-btn>

        <v-btn
          variant="outlined"
          color="#8a8a9a"
          class="text-none"
          rounded="lg"
          prepend-icon="mdi-delete-empty"
          size="small"
          @click="clearFilters"
        >
          <span class="btn-text">Clear Filters</span>
        </v-btn>

        <v-spacer />

        <v-btn
          color="error"
          variant="flat"
          class="text-none"
          size="small"
          rounded="lg"
          :disabled="selectedTable.length === 0"
          @click="deleteMultiple()"
        >
          <span class="btn-text">
            <v-icon class="mx-1">mdi-delete</v-icon>
            Delete Multiple{{
              selectedTable.length ? ` (${selectedTable.length})` : ""
            }}
          </span>
        </v-btn>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-card variant="flat" class="custom-card" rounded="lg">
      <v-data-table
        v-model="selectedTable"
        show-select
        :items="subscribers || []"
        :headers="headers"
        item-value="id"
        :search="search"
        density="compact"
        hide-default-footer
        items-per-page="-1"
      >
        <!-- Full Name -->
        <template #[`item.fullName`]="{ item }">
          <a :href="'/user/' + item.id" class="chip-text">
            {{ item.first_name }} {{ item.last_name }}
          </a>
        </template>

        <!-- Subscription Status -->
        <template #[`item.status`]="{ item }">
          <v-chip
            :color="getColor(item.raw.is_subscribed)"
            variant="flat"
            size="x-small"
            class="chip-text"
          >
            {{ item.raw.is_subscribed ? "subscribed" : "unsubscribed" }}
          </v-chip>
        </template>

        <!-- Blacklist Status -->
        <template #[`item.blacklist`]="{ item }">
          <v-chip
            v-if="item.raw.is_blacklisted"
            color="black"
            variant="flat"
            size="x-small"
            class="chip-text"
          >
            blacklisted
          </v-chip>
        </template>

        <!-- Groups -->
        <template #[`item.groups`]="{ item }">
          <span>{{ item.raw.groups?.[0]?.name || "" }}</span>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{ item }">
          <div class="d-flex align-center ga-2">
            <v-tooltip location="bottom" text="Edit Subscriber">
              <template #activator="{ props }">
                <!-- <v-icon
                  v-bind="props"
                  size="18"
                  class="cursor-pointer text-primary mx-1"
                  @click="$router.push({ name: 'AddSubscriber', params: { id: item.raw.id } })"
                >
                  mdi-pencil
                </v-icon> -->

                <v-icon
                  v-bind="props"
                  size="18"
                  class="cursor-pointer text-primary mx-1"
                  @click="openEditModal(item.raw.id)"
                >
                  mdi-pencil
                </v-icon>
              </template>
            </v-tooltip>

            <v-tooltip
              location="bottom"
              :text="item.raw.is_subscribed ? 'Unsubscribe' : 'Subscribe'"
            >
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  size="18"
                  class="cursor-pointer mx-1"
                  :class="
                    item.raw.is_subscribed ? 'text-warning' : 'text-success'
                  "
                  @click="
                    item.raw.is_subscribed
                      ? unSubscribe(item.raw.id, item.raw.email)
                      : Subscribe(item.raw.id, item.raw.email)
                  "
                >
                  {{
                    item.raw.is_subscribed ? "mdi-bell-off" : "mdi-bell-plus"
                  }}
                </v-icon>
              </template>
            </v-tooltip>

            <v-tooltip location="bottom" text="Blacklist Subscriber">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  v-if="!item.raw.is_blacklisted"
                  size="18"
                  class="cursor-pointer text-deep-orange mx-1"
                  @click="Blacklist(item.raw.id, item.raw.email)"
                >
                  mdi-account-cancel
                </v-icon>
              </template>
            </v-tooltip>

            <v-tooltip location="bottom" text="Delete Subscriber">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  size="18"
                  class="cursor-pointer text-error mx-1"
                  @click="deleteSub(item.raw.id, item.raw.email)"
                >
                  mdi-delete
                </v-icon>
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>

      <v-divider />

      <div class="d-flex justify-end pa-4">
        <PaginationControls
          :current-page="currentPage"
          :total-pages="pagination ? pagination.total_pages : 0"
          @page-changed="fetchSubscribers"
        />
      </div>
    </v-card>

    <!-- Loading dialog -->
    <v-dialog v-model="loaderState" max-width="400" persistent>
      <v-card class="pa-4 text-center" rounded="lg">
        <v-card-text>Please wait&hellip;</v-card-text>
        <v-card-actions class="justify-center">
          <div class="ball"></div>
          <div class="ball"></div>
          <div class="ball"></div>
          <div class="ball"></div>
          <div class="ball"></div>
        </v-card-actions>
        <v-progress-linear
          class="mx-2 mt-2"
          color="teal"
          indeterminate
        ></v-progress-linear>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
      persistent
      transition="fade-transition"
    >
      <v-card class="rounded-lg" elevation="8">
        <v-card-title class="text-h5 font-weight-bold d-flex align-center">
          <v-icon start color="error" size="large" class="me-2"
            >mdi-alert-circle</v-icon
          >
          Warning
        </v-card-title>

        <v-card-text class="text-body-1 py-4">
          Are you sure you want to delete {{ selectedTable.length }} selected
          subscriber{{ selectedTable.length === 1 ? "" : "s" }}? This can't be
          undone.
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn
            color="error"
            variant="outlined"
            @click="deleteDialog = false"
            class="text-none font-weight-medium"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            variant="elevated"
            @click="deleteMultiple2()"
            class="text-none font-weight-medium"
          >
            Proceed
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <addSubscriber
      v-model="showSubscriberModal"
      :subscriber-id="editingSubscriberId"
      @saved="onSubscriberSaved"
    />
  </div>
</template>

<script>
import PaginationControls from "@/utils/PaginationControls.vue";
import addSubscriber from "./addSubscriber.vue";
export default {
  name: "SubListPage",
  beforeRouteEnter(to, from, next) {
    next((v) => {
      v.$store.dispatch("subscribers/getFilteredSubscribers", v.formData);
      v.$store.dispatch("subscribers/getLists");
    });
  },

  components: {
    PaginationControls,
    addSubscriber,
  },

  data() {
    return {
      showSubscriberModal: false,
      editingSubscriberId: null,

      formData: {
        is_subscribed: false,
        unsubscribed: false,
        is_blacklisted: false,
        email: null,
        group_id: null,
        offset: null,
        limit: null,
        current_page: 1,
      },
      deleteDialog: false,
      totalSubscribers: 0,
      selectedTable: [],
      selectedGroup: null,
      subStatus: null,
      search: "",
      isValid: false,
      loadingState: false,
      screenSize: "",
      oldValue: false,

      headers: [
        { title: "Created At", key: "created_at" },
        { title: "Email", key: "email" },
        {
          title: "Status",
          key: "status",
          value: (item) => `${item.is_subscribed}`,
        },
        {
          title: "Name",
          key: "fullName",
          value: (item) => `${item.first_name} ${item.last_name}`,
        },
        {
          title: "BlackList",
          key: "blacklist",
          value: (item) => `${item.is_blacklisted}`,
        },
        { title: "Group name", key: "groups" },
        { title: "Actions", key: "actions" },
      ],

      loading: false,
      showButton: false,
      page: 1,
      itemPage: 0,
      currentValue: 0,
    };
  },

  computed: {
    lists() {
      return this.$store.getters["subscribers/subscribersGetters"]("Lists");
    },
    subscribers() {
      return this.$store.getters["subscribers/subscribersGetters"](
        "subscribers",
      );
    },
    pagination() {
      return this.$store.getters["subscribers/subscribersGetters"](
        "pagination",
      );
    },
    currentPage() {
      return this.pagination ? this.pagination.current_page : 1;
    },
    pageSize() {
      return this.pagination ? this.pagination.page_size : 10;
    },
    loaderState() {
      return this.$store.getters["subscribers/subscribersGetters"](
        "loaderState",
      );
    },
    next() {
      return this.$store.getters["subscribers/subscribersGetters"]("next");
    },
    previous() {
      return this.$store.getters["subscribers/subscribersGetters"]("previous");
    },
    limit() {
      return this.$store.getters["subscribers/subscribersGetters"]("limit");
    },
  },

  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },

  methods: {
    checkScreenSize() {
      const isSmallScreen = window.matchMedia("(max-width: 768px)").matches;
      this.screenSize = isSmallScreen ? "Small" : "Large";
    },

    deleteSub(val, val2) {
      if (confirm(`Are you sure you want to delete subscriber: ${val2}`)) {
        this.$store.dispatch("subscribers/deleteSubscriber", parseInt(val));
      }
    },

    unSubscribe(val, val2) {
      if (confirm(`Are you sure you want to unsubscribe subscriber: ${val2}`)) {
        this.$store.dispatch("subscribers/unsubSubscriber", {
          subdata: { is_subscribed: false },
          id: parseInt(val),
        });
      }
    },

    Subscribe(val, val2) {
      if (confirm(`Are you sure you want to resubscribe subscriber: ${val2}`)) {
        this.$store.dispatch("subscribers/unsubSubscriber", {
          subdata: { is_subscribed: true },
          id: parseInt(val),
        });
      }
    },

    Blacklist(val, val2) {
      if (confirm(`Are you sure you want to blacklist subscriber: ${val2}`)) {
        this.$store.dispatch("subscribers/unsubSubscriber", {
          subdata: { is_blacklisted: true },
          id: parseInt(val),
        });
      }
    },

    getColor(val) {
      return val ? "#46c35f" : "orange";
    },

    redirectToAdd() {
      this.editingSubscriberId = null;
      this.showSubscriberModal = true;
    },

    openEditModal(id) {
      this.editingSubscriberId = id;
      this.showSubscriberModal = true;
    },

    onSubscriberSaved() {
      // Refresh the table after add/edit so the new/updated row shows up
      this.$store.dispatch("subscribers/getFilteredSubscribers", this.formData);
    },

    deleteMultiple() {
      if (this.selectedTable.length === 0) return;
      this.deleteDialog = true;
    },

    deleteMultiple2() {
      this.$store.dispatch("subscribers/deleteMultiple", {
        ids: [...this.selectedTable],
      });
      this.deleteDialog = false;
      this.selectedTable = [];
    },

    submitFilters() {
      this.formData.offset = null;
      this.formData.current_page = 1;
      this.$store.dispatch("subscribers/getFilteredSubscribers", this.formData);
    },

    clearFilters() {
      this.formData = {
        is_subscribed: false,
        unsubscribed: false,
        is_blacklisted: false,
        email: null,
        group_id: null,
        offset: null,
        limit: this.formData.limit,
        current_page: 1,
      };
      this.$store.dispatch("subscribers/getFilteredSubscribers", this.formData);
    },

    async fetchSubscribers(pageNumber) {
      const offset = (pageNumber - 1) * this.pageSize;
      this.formData.offset = offset;
      this.formData.limit = this.pageSize;
      this.formData.current_page = pageNumber;
      this.$store.dispatch("subscribers/getFilteredSubscribers", this.formData);
    },
  },

  watch: {
    loaderState: {
      handler: function (newValue, oldValue) {
        this.oldValue = oldValue;
        this.loadingState = newValue;
      },
      immediate: true,
    },
  },
};
</script>

<style>
@import url("../subscriberStyle.css");
</style>

<style scoped>
.header-card {
  box-shadow: 0 4px 16px -8px rgba(67, 56, 118, 0.3) !important;
}

.head-sub-text {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.filter-card,
.custom-card {
  box-shadow: 0 4px 16px -8px rgba(45, 42, 74, 0.08) !important;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b6b80;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.status-check {
  margin-right: 8px;
}

.chip-text {
  font-size: 12px;
}
</style>
